// Cropping, turning and toning, the same on the server and in the app. The original (upright, as the browser draws
// it) is first turned a quarter at a time clockwise (`turn`, 0 to 3); a crop is the part of that which shows, in whole
// pixels, in color or the `tone` chosen. The original file is never
// changed; the copies people see are made from it with all of this applied, and a book printed from the original
// applies the same.

export type Crop = { x: number; y: number; width: number; height: number; turn?: Turn; tone?: Tone }
export type Turn = 0 | 1 | 2 | 3
export type Tone = 'mono' | 'sepia'
export const TONES: readonly Tone[] = ['mono', 'sepia']

// How big the original is once turned: a quarter turn swaps its sides.
export const turnedSize = (width: number, height: number, turn: number = 0) =>
  turn % 2 ? { width: height, height: width } : { width, height }

// The biggest part of the given shape (width over height) the photo holds, centred.
export function ratioCrop(width: number, height: number, ratio: number): Crop {
  const w = Math.min(width, Math.round(height * ratio))
  const h = Math.min(height, Math.round(w / ratio))
  return { x: Math.floor((width - w) / 2), y: Math.floor((height - h) / 2), width: w, height: h }
}

// The biggest square the photo holds, centred: as wide as the photo when it stands tall, as tall when it lies wide.
export const squareCrop = (width: number, height: number): Crop => ratioCrop(width, height, 1)

export const wholeCrop = (width: number, height: number): Crop => ({ x: 0, y: 0, width, height })

// Whether a crop leaves the photo exactly as it was: not turned, in color, all of it showing.
export const isWhole = (crop: Crop, width: number, height: number) =>
  !crop.turn && !crop.tone && crop.x === 0 && crop.y === 0 && crop.width === width && crop.height === height

// The photo turned a quarter clockwise (by 1) or anticlockwise (by -1) as it shows, keeping the same part of it.
// `width` and `height` are the original's.
export function turnCrop(crop: Crop, width: number, height: number, by: 1 | -1): Crop {
  const frame = turnedSize(width, height, crop.turn)
  const turn = ((((crop.turn ?? 0) + by) % 4) + 4) % 4
  const box =
    by === 1
      ? { x: frame.height - crop.y - crop.height, y: crop.x }
      : { x: crop.y, y: frame.width - crop.x - crop.width }
  return tidy({ ...box, width: crop.height, height: crop.width, turn: turn as Turn, tone: crop.tone })
}

// No turn and color are left out, so a crop that does neither reads as it always has.
function tidy({ turn, tone, ...box }: Crop): Crop {
  return { ...box, ...(turn ? { turn } : {}), ...(tone ? { tone } : {}) }
}

// The smallest crop kept, in pixels along either side: smaller than this is a slip, not a picture.
export const MIN_CROP = 16

// A crop as sent, checked against the photo it's for (the original's size): whole pixels, inside the
// photo as turned, not too small, turned by whole quarters, in color or a known tone. Anything else
// is null.
export function readCrop(value: unknown, originalWidth: number, originalHeight: number): Crop | null {
  if (typeof value !== 'object' || value === null) return null
  const { x, y, width: w, height: h, turn = 0, tone } = value as Record<string, unknown>
  const whole = [x, y, w, h].every((n) => Number.isSafeInteger(n))
  if (!whole) return null
  if (turn !== 0 && turn !== 1 && turn !== 2 && turn !== 3) return null
  if (tone !== undefined && !TONES.includes(tone as Tone)) return null
  const crop = tidy({ x, y, width: w, height: h, turn, tone } as Crop)
  const { width, height } = turnedSize(originalWidth, originalHeight, turn)
  if (crop.x < 0 || crop.y < 0) return null
  if (crop.width < Math.min(MIN_CROP, width) || crop.height < Math.min(MIN_CROP, height)) return null
  if (crop.x + crop.width > width || crop.y + crop.height > height) return null
  return crop
}

// How big a photo shows once cropped.
export const shownSize = (photo: { width: number; height: number; crop?: Crop | null }) =>
  photo.crop ? { width: photo.crop.width, height: photo.crop.height } : { width: photo.width, height: photo.height }

// A photo's "ref": its id, and once its copies have been made again (cropped), when that was ("<id>.<edited>"). The
// app builds a photo's address from its ref, so a crop gives the copies a new address and no browser keeps showing
// the old ones. Ids never hold a dot.
export const photoRef = (id: string, editedAt: number | null | undefined) => (editedAt ? `${id}.${editedAt}` : id)

export function readRef(ref: string) {
  const [id = '', edited] = ref.split('.')
  return { id, v: edited ?? null }
}
